import React, { FC, ReactNode, Ref } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { up } from '../../core/utils/Breakpoints';
import { gridColumns, gridGutters } from '../../core/styles/theme/layout';
import { Fade, FadeProps } from 'react-awesome-reveal';

const calcGridGutters = (gutters: any) => {
  let style = '';

  if (typeof gutters === 'object') {
    Object.keys(gutters).forEach((key) => {
      style += `
			${up(key)} {
				width: calc(100% + ${rem(gutters[key])});
				margin-left: calc(${rem(gutters[key])} / -2);
				margin-right: calc(${rem(gutters[key])} / -2);
			}`;
    });
  } else {
    style = `
			width: calc(100% + ${rem(gutters)});
			margin-left: calc(${rem(gutters)} / -2);
			margin-right: calc(${rem(gutters)} / -2);
		`;
  }

  return style;
};

const calcGridItemBreakpoints = (
  size: any,
  push: any,
  pull: any,
  columns: any,
  gutters: any,
  gridWidth: any,
  pushPullWidth: any,
) => {
  const pushStyle = (key: any) =>
    pushPullWidth
      ? `left: calc(${(push[key] || 0) / columns[key]} * ${rem(
          pushPullWidth,
        )});`
      : `left: calc(${((push[key] || 0) / columns[key]) * 100}%);`;
  const pullStyle = (key: any) =>
    pushPullWidth
      ? `right: calc(${(pull[key] || 0) / columns[key]} * ${rem(
          pushPullWidth,
        )});`
      : `right: calc(${((pull[key] || 0) / columns[key]) * 100}%);`;
  let style = '';

  Object.keys(columns).forEach((key) => {
    if (size[key] === 'auto') {
      style += `
				${up(key)} {
					flex-grow: 1;
					flex-shrink: 0;
					flex-basis: 0;
					${
            gridWidth
              ? `width: calc(${1 / columns[key]} * ${rem(gridWidth)} - ${rem(
                  gutters[key] || gutters,
                )});`
              : `min-width: calc(${(1 / columns[key]) * 100}% - ${rem(
                  gutters[key] || gutters,
                )});`
          }
					margin-left: calc(${rem(gutters[key] || gutters)} / 2);
					margin-right: calc(${rem(gutters[key] || gutters)} / 2);
					${push ? pushStyle(key) : ''}
					${pull ? pullStyle(key) : ''}
				}`;
    } else {
      size[key] == 'full' ? (size[key] = 1) : size[key];
      columns[key] == 'full' ? (columns[key] = 1) : columns[key];
      style += `
				${up(key)} {
				${
          gridWidth
            ? `width: calc(${size[key] / columns[key]} * ${rem(
                gridWidth,
              )} - ${rem(gutters[key] || gutters)});`
            : `flex-grow: 0;
						flex-shrink: 1;
						flex-basis: calc(${(size[key] / columns[key]) * 100}% - ${rem(
                gutters[key] || gutters,
              )});`
        }
					margin-left: calc(${rem(gutters[key] || gutters)} / 2);
					margin-right: calc(${rem(gutters[key] || gutters)} / 2);
					${push ? pushStyle(key) : ''}
					${pull ? pullStyle(key) : ''}
				}`;
    }
  });

  return style;
};

const calcSize = (cols: any, size: any) => {
  const mergedSize = { ...cols };
  const estimatedItemsPerRow: any = {};

  // size === 'full' => we keep original value of columns
  if (size === 'auto') {
    Object.keys(mergedSize).forEach((key) => {
      mergedSize[key] = 'auto';
      estimatedItemsPerRow[key] = 1;
    });
  } else if (typeof size === 'number') {
    Object.keys(mergedSize).forEach((key) => {
      mergedSize[key] = size;
      estimatedItemsPerRow[key] = cols[key] / mergedSize[key];
    });
  } else if (typeof size === 'object' && size !== null) {
    let currentValue: any;
    let isFull = false;

    Object.keys(mergedSize).forEach((key) => {
      if (size[key] === 'full') {
        isFull = true;
      } else if (size[key] && size[key] !== 'full') {
        isFull = false;
      }

      currentValue = (isFull ? mergedSize[key] : size[key]) || currentValue;

      mergedSize[key] = currentValue || mergedSize[key];

      // dont let an item to take more columns than available
      if (mergedSize[key] > cols[key]) {
        mergedSize[key] = cols[key];
      }

      estimatedItemsPerRow[key] =
        mergedSize[key] === 'auto' ? 1 : cols[key] / mergedSize[key];
    });
  }

  return { size: mergedSize, estimatedItemsPerRow };
};

const calcPushPull = (cols: any, pushPull: any) => {
  let mergedPushPull = { ...cols };

  if (pushPull === null) {
    mergedPushPull = null;
  } else if (typeof pushPull === 'number') {
    Object.keys(mergedPushPull).forEach((key) => {
      mergedPushPull[key] = pushPull;
    });
  } else if (typeof pushPull === 'object') {
    let currentValue = 0;

    Object.keys(mergedPushPull).forEach((key) => {
      currentValue =
        typeof pushPull[key] === 'undefined' ? currentValue : pushPull[key];

      mergedPushPull[key] = currentValue;
    });
  }

  return mergedPushPull;
};

const calcColumns = (columns: any) => {
  const mergedCols: any = { ...gridColumns };
  const gridColumnConfig: any = gridColumns;

  if (typeof columns === 'number') {
    Object.keys(mergedCols).forEach((key) => {
      mergedCols[key] = columns;
    });
  } else if (typeof columns === 'object') {
    let currentValue: any;

    Object.keys(mergedCols).forEach((key) => {
      currentValue = columns[key] || currentValue;
      mergedCols[key] = currentValue || mergedCols[key];

      // replace auto values with default values
      if (mergedCols[key] === 'auto') {
        mergedCols[key] = gridColumnConfig[key];
      }
    });
  }

  return mergedCols;
};

const StyledGrid = styled.div(
  ({ gutters }: GridProps) => css`
    position: relative;
    display: flex;
    flex-wrap: wrap;

    ${calcGridGutters(gutters)}
  `,
);

const StyledGridItem = styled.div(
  ({
    size,
    push,
    pull,
    columns,
    gutters,
    gridWidth,
    pushPullWidth,
  }: GridItemProps) => css`
    position: relative;
    box-sizing: border-box;
    /* https://css-tricks.com/flexbox-truncated-text/ */
    min-width: 0;

    ${calcGridItemBreakpoints(
      size,
      push,
      pull,
      columns,
      gutters,
      gridWidth,
      pushPullWidth,
    )}
  `,
);

const StyledGridItemAnimated = styled(Fade)<
  FadeProps & GridItemProps & { ref: React.RefObject<HTMLElement> }
>(
  ({ size, push, pull, columns, gutters, gridWidth, pushPullWidth }) => css`
    position: relative;
    box-sizing: border-box;
    /* https://css-tricks.com/flexbox-truncated-text/ */
    min-width: 0;

    ${calcGridItemBreakpoints(
      size,
      push,
      pull,
      columns,
      gutters,
      gridWidth,
      pushPullWidth,
    )}
  `,
);

const Grid: FC<GridProps> = ({
  columns = gridColumns,
  gutters = gridGutters,
  gridRef,
  children,
  ...rest
}: GridProps) => {
  const cols = calcColumns(columns);

  return (
    <StyledGrid columns={cols} gutters={gutters} ref={gridRef} {...rest}>
      {children &&
        React.Children.toArray(children)
          // Remove nulls from children to avoid null reference errors when calculating size, push and pull in the map function afterwards
          .filter((child) => typeof child === 'object')
          .map((child: any) => {
            const { size } = calcSize(cols, child.props.size || 'auto');
            const push = child.props.push
              ? calcPushPull(cols, child.props.push)
              : null;
            const pull = child.props.pull
              ? calcPushPull(cols, child.props.pull)
              : null;

            const childProps = {
              columns: cols,
              gutters,
              size,
              push,
              pull,
            };

            return React.cloneElement(child, { ...childProps });
          })}
    </StyledGrid>
  );
};

const GridItem: FC<GridItemProps> = ({
  size = 'auto',
  columns = gridColumns,
  gutters = gridGutters,
  calcEstimatedItemsPerRow,
  gridItemRef,
  children,
  ...rest
}) => {
  const { size: itemSize, estimatedItemsPerRow } = calcSize(columns, size);

  return (
    <StyledGridItem
      size={itemSize}
      columns={columns}
      gutters={gutters}
      ref={gridItemRef}
      {...rest}
    >
      {children &&
        React.Children.map(children, (child: any) => {
          const columns = child && child.props && child.props.columns;
          const hasColumns = !!columns;
          const shouldClone = hasColumns || calcEstimatedItemsPerRow;

          return shouldClone
            ? React.cloneElement(child, {
                columns: hasColumns ? calcColumns(columns) : undefined,
                gutters: hasColumns ? gutters : undefined,
                estimatedItemsPerRow: calcEstimatedItemsPerRow
                  ? estimatedItemsPerRow
                  : undefined,
              })
            : child;
        })}
    </StyledGridItem>
  );
};

const GridItemAnimated: FC<GridItemProps & FadeProps> = ({
  size = 'auto',
  columns = gridColumns,
  gutters = gridGutters,
  calcEstimatedItemsPerRow,
  gridItemRef,
  children,
  ...rest
}) => {
  const { size: itemSize, estimatedItemsPerRow } = calcSize(columns, size);

  return (
    <StyledGridItemAnimated
      size={itemSize}
      columns={columns}
      gutters={gutters}
      ref={gridItemRef}
      {...rest}
    >
      {children &&
        React.Children.map(children, (child: any) => {
          const columns = child && child.props && child.props.columns;
          const hasColumns = !!columns;
          const shouldClone = hasColumns || calcEstimatedItemsPerRow;

          return shouldClone
            ? React.cloneElement(child, {
                columns: hasColumns ? calcColumns(columns) : undefined,
                gutters: hasColumns ? gutters : undefined,
                estimatedItemsPerRow: calcEstimatedItemsPerRow
                  ? estimatedItemsPerRow
                  : undefined,
              })
            : child;
        })}
    </StyledGridItemAnimated>
  );
};

export type GridProps = {
  /** Number of Columns the Grid have */
  columns?: number | object;
  /** Gutter between the Columns */
  gutters?: number | string | object;
  /** ref of the Grid - for internal use */
  gridRef?: Ref<any>;
  /** Children of the Grid - use GridItem */
  children?: ReactNode;
  /** Class name for grid */
  className?: string;
};

export type GridItemProps = {
  /** Size of the GridItem in conjunction with the available columns in the Grid */
  size?: any;
  /** Number of Columns the Grid have */
  columns?: number | object;
  /** Gutter between the Columns */
  gutters?: number | string | object;
  /** Number of columns the GridItem should be pushed */
  push?: number | object;
  /** Number of columns the GridItem should be pulled */
  pull?: number | object;
  /** Use a fixed width for the GridItem */
  gridWidth?: number;
  /** Use a fixed number for the push or pull of the GridItem */
  pushPullWidth?: number;
  /** Calculate the estimated number of GridItems that will fit into one row */
  calcEstimatedItemsPerRow?: boolean;
  /** ref of the GridItem - for internal use */
  gridIAtemRef?: Ref<any>;
  /** Children of the GridItem */
  children?: ReactNode;
  /** Reference for animation */
  gridItemRef?: any;
  cascade?: any;
  damping?: any;
  duration?: any;
  triggerOnce?: any;
};

export { Grid, GridItem, GridItemAnimated };
